import React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useProfile } from '../../contexts/ProfileContext';
import { useAuth } from '../../contexts/AuthContext';
import { StartupProfile, InvestorProfile } from '../../types/firestore';
import ProfileBreadcrumbs from './ProfileBreadcrumbs';
import { maxReachableWizardStep } from '../../utils/wizardStepAccess';
import ProfileStep from './ProfileStep';
import ProfileNavigation from './ProfileNavigation';
import UnsavedProfileChangesDialog from './UnsavedProfileChangesDialog';
import { IMPRESSIVE_FACTOR_MIN_CHARS } from '../../constants/profileFieldLimits';
import { countryRequiresStateSubdivision } from '../../utils/investorPricingView';

type PendingLeave =
  | { type: 'step'; index: number }
  | { type: 'href'; href: string; state?: unknown }
  | { type: 'history' }
  | { type: 'callback'; onAllow: () => void };

/** Lets a parent (e.g. Settings) ask before leaving an embedded wizard with unsaved edits. */
export type ProfileWizardLeaveGuard = {
  tryLeave: (onAllow: () => void) => void;
};

function stableStringify(value: unknown): string {
  if (value === undefined) return 'null';
  if (value === null || typeof value !== 'object') return JSON.stringify(value) ?? 'null';
  if (Array.isArray(value)) {
    return `[${value.map(item => stableStringify(item)).join(',')}]`;
  }
  const record = value as Record<string, unknown>;
  const keys = Object.keys(record)
    .filter(key => record[key] !== undefined)
    .sort();
  return `{${keys.map(key => `${JSON.stringify(key)}:${stableStringify(record[key])}`).join(',')}}`;
}

function historyUrl(url: string | URL | null | undefined): string | null {
  if (url == null || url === '') return null;
  const raw = String(url);
  try {
    const parsed = new URL(raw, window.location.origin);
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return raw;
  }
}

interface ProfileWizardProps {
  userType: 'startup' | 'investor';
  embedded?: boolean;
  onSave?: (updatedProfile: StartupProfile) => void;
  leaveGuardRef?: React.MutableRefObject<ProfileWizardLeaveGuard | null>;
}

const ProfileWizard: React.FC<ProfileWizardProps> = ({
  userType,
  embedded = false,
  onSave,
  leaveGuardRef,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { step: stepParam } = useParams<{ step?: string }>();

  // Profile context hooks
  const { startupProfile, investorProfile, updateProfile, createProfile, loading } = useProfile();
  const { user } = useAuth();

  // Get the correct profile based on userType
  const currentProfile = userType === 'startup' ? startupProfile : investorProfile;

  // Track initial load to prevent showing empty form before data is fetched
  const [isInitialLoad, setIsInitialLoad] = React.useState(true);

  // Define steps based on userType (moved up so we can use it in step calculation)
  // Memoize to prevent recreation on every render
  const steps = React.useMemo(
    () =>
      userType === 'startup'
        ? [
            {
              id: 'company',
              title: 'Company',
              description: 'Basic company information',
            },
            {
              id: 'industry-stage',
              title: 'Industry, Stage & Investment',
              description: 'Business details and funding',
            },
            {
              id: 'founders-team',
              title: 'Founders & Team',
              description: 'Team composition and leadership',
            },
            {
              id: 'ownership-traction',
              title: 'Ownership & Traction',
              description: 'Equity and business metrics',
            },
            {
              id: 'external-support',
              title: 'External Support',
              description: 'Advisors and partnerships',
            },
            {
              id: 'matching-starts',
              title: 'Matching Starts',
              description: 'Complete profile and start matching',
            },
          ]
        : [
            // Investor steps - same structure as startup but different content
            {
              id: 'company',
              title: 'Company',
              description: 'Basic company information',
            },
            {
              id: 'industry-stage',
              title: 'Industry, Stage & Investment',
              description: 'Business details and funding',
            },
            {
              id: 'founders-team',
              title: 'Founders & Team',
              description: 'Team composition and leadership',
            },
            {
              id: 'ownership-traction',
              title: 'Ownership & Traction',
              description: 'Equity and business metrics',
            },
            {
              id: 'external-support',
              title: 'External Support',
              description: 'Advisors and partnerships',
            },
            {
              id: 'matching-starts',
              title: 'Matching Starts',
              description: 'Complete profile and start matching',
            },
          ],
    [userType]
  );

  // Map step ID from URL to step index - memoized to prevent recreation
  const getStepIndexFromUrl = React.useCallback(
    (stepId: string | undefined): number => {
      if (!stepId) return 0;
      const index = steps.findIndex(s => s.id === stepId);
      return index >= 0 ? index : 0;
    },
    [steps]
  );

  // Check if there's a forced step from navigation state
  const forcedStep = (location.state as any)?.forceStep;
  // editMode only applies when a step slug is in the URL (prevents blocking matching-starts at /profile/investor)
  const editMode = Boolean(stepParam && (location.state as any)?.editMode);

  const matchingStartsStepIndex = steps.length - 1;

  const isProfileComplete = React.useCallback(
    (profile: StartupProfile | InvestorProfile | null): boolean => {
      if (!profile) return false;
      const lastCompleted = 'lastCompletedStep' in profile ? (profile.lastCompletedStep ?? -1) : -1;
      const current = 'currentStep' in profile ? (profile.currentStep ?? 0) : 0;
      return (
        profile.profileCompleted === true ||
        lastCompleted >= matchingStartsStepIndex ||
        current >= matchingStartsStepIndex
      );
    },
    [matchingStartsStepIndex]
  );

  // Initialize currentStep from URL parameter first, then forcedStep, then profile, then 0
  // Embedded mode always starts at step 0 (no URL to read from)
  const initialStep = embedded ? 0 : stepParam ? getStepIndexFromUrl(stepParam) : (forcedStep ?? 0);

  const [currentStep, setCurrentStep] = React.useState(initialStep);

  // Use ref to track if we've done the initial step setup (ref doesn't trigger re-renders)
  const hasInitializedStepRef = React.useRef(false);
  // Use ref to track if we're programmatically navigating (to prevent effect loops)
  const isNavigatingRef = React.useRef(false);
  // Track the path we're navigating to
  const navigatingToPathRef = React.useRef<string | null>(null);
  // Track the last path we navigated to (to prevent duplicate navigations)
  const lastNavigatedPathRef = React.useRef<string | null>(null);
  // Debounce timer for navigate calls
  const navigateTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  // Simple initialization - just mark as initialized when component mounts
  // No auto-navigation to last step - user always starts where they navigate to
  React.useEffect(() => {
    if (!hasInitializedStepRef.current) {
      hasInitializedStepRef.current = true;
      // console.log('✅ ProfileWizard initialized at step:', currentStep);
    }
  }, [currentStep]);

  // Update currentStep when URL parameter changes
  // THIS IS THE SINGLE SOURCE OF TRUTH: URL → State
  // When handleSave navigates, this effect updates currentStep automatically
  React.useEffect(() => {
    // Embedded mode uses local state only — no URL sync needed
    if (embedded) return;

    // Skip if still loading (to avoid race conditions)
    if (loading) {
      return;
    }

    // Skip if we haven't initialized yet
    if (!hasInitializedStepRef.current) {
      return;
    }

    // No step slug in URL — let profile sync pick the right step (e.g. matching-starts for completed profiles)
    if (!stepParam) {
      return;
    }

    const stepIndex = getStepIndexFromUrl(stepParam);

    // Only update if stepIndex is different from current step
    // Use functional update to avoid dependency on currentStep
    setCurrentStep((prevStep: number) => {
      if (stepIndex !== prevStep) {
        // console.log('🔄 URL → State: Updating currentStep from', prevStep, 'to', stepIndex);
        // Reset navigation flag when we successfully update from URL
        if (isNavigatingRef.current && navigatingToPathRef.current === location.pathname) {
          isNavigatingRef.current = false;
          navigatingToPathRef.current = null;
        }
        return stepIndex;
      }
      return prevStep;
    });
  }, [stepParam, location.pathname, getStepIndexFromUrl, loading, embedded]);

  // Completed profiles visiting /profile/investor (no step slug) → dashboard or matching-starts URL
  React.useLayoutEffect(() => {
    if (embedded || stepParam || editMode || loading || !currentProfile) return;
    if (!isProfileComplete(currentProfile)) return;

    if (userType === 'investor') {
      navigate('/dashboard', { replace: true });
      return;
    }

    const matchingStartsPath = `/profile/${userType}/matching-starts`;
    if (location.pathname !== matchingStartsPath) {
      navigate(matchingStartsPath, { replace: true });
    }
  }, [
    embedded,
    stepParam,
    editMode,
    loading,
    currentProfile,
    userType,
    location.pathname,
    navigate,
    isProfileComplete,
  ]);

  // Resolve step from profile when URL has no step slug (in-progress profiles only)
  React.useEffect(() => {
    if (embedded || stepParam || editMode) return;
    if (isNavigatingRef.current) return;
    if (!currentProfile || loading) return;
    if (isProfileComplete(currentProfile)) {
      setCurrentStep(matchingStartsStepIndex);
      return;
    }

    if ('currentStep' in currentProfile && currentProfile.currentStep != null) {
      setCurrentStep(currentProfile.currentStep ?? 0);
      return;
    }

    const lastCompleted =
      userType === 'startup'
        ? ((currentProfile as StartupProfile).lastCompletedStep ?? -1)
        : ((currentProfile as InvestorProfile).lastCompletedStep ?? -1);

    if (lastCompleted >= 0) {
      setCurrentStep(lastCompleted + 1);
    }
  }, [
    currentProfile,
    userType,
    stepParam,
    loading,
    editMode,
    matchingStartsStepIndex,
    embedded,
    isProfileComplete,
  ]);

  // State for current form data
  const [currentFormData, setCurrentFormData] = React.useState<any>(null);

  // Mark initial load as complete when profile loading has finished
  React.useEffect(() => {
    if (user && !loading) {
      // User exists and loading has completed (profile either exists or doesn't exist)
      setIsInitialLoad(false);
    }
  }, [loading, user]);

  const [shouldValidate, setShouldValidate] = React.useState(false);
  const [validationError, setValidationError] = React.useState<string | null>(null);
  const [isSaving, setIsSaving] = React.useState(false);
  const [isDirty, setIsDirty] = React.useState(false);
  const [leaveDialogOpen, setLeaveDialogOpen] = React.useState(false);
  const dirtyFrozenRef = React.useRef(false);
  const baselineRef = React.useRef<string | null>(null);
  const isDirtyRef = React.useRef(false);
  const userEditedRef = React.useRef(false);
  const allowLeaveRef = React.useRef(false);
  const pendingLeaveRef = React.useRef<PendingLeave | null>(null);
  const leaveRequestRef = React.useRef(0);
  const trapPushedRef = React.useRef(false);
  const wizardUrlRef = React.useRef('');

  const hasUnsavedChanges = React.useCallback(
    () => isDirtyRef.current || userEditedRef.current,
    []
  );

  React.useEffect(() => {
    dirtyFrozenRef.current = false;
    baselineRef.current = null;
    isDirtyRef.current = false;
    userEditedRef.current = false;
    trapPushedRef.current = false;
    setIsDirty(false);
  }, [currentStep]);

  const markDirty = React.useCallback((changed: boolean) => {
    if (isDirtyRef.current === changed) return;
    isDirtyRef.current = changed;
    setIsDirty(changed);
  }, []);

  const noteUserEdit = React.useCallback(
    (event: React.SyntheticEvent) => {
      dirtyFrozenRef.current = true;
      if (event.type === 'input' || event.type === 'change') {
        // Native <select> needs a frame to commit before a parent re-render,
        // otherwise macOS often drops the first pick (looks like a double-click bug).
        const tag = (event.target as HTMLElement | null)?.tagName;
        if (tag === 'SELECT') {
          window.requestAnimationFrame(() => {
            userEditedRef.current = true;
            markDirty(true);
          });
          return;
        }
        userEditedRef.current = true;
        markDirty(true);
        return;
      }
      const target = event.target as HTMLElement | null;
      if (!target?.closest('button, [role="switch"], [role="checkbox"], [role="radio"]')) return;
      userEditedRef.current = true;
      markDirty(true);
    },
    [markDirty]
  );

  const queueLeave = React.useCallback((leave: PendingLeave) => {
    pendingLeaveRef.current = leave;
    setLeaveDialogOpen(true);
  }, []);

  React.useEffect(() => {
    if (!leaveGuardRef) return;
    leaveGuardRef.current = {
      tryLeave: onAllow => {
        if (!hasUnsavedChanges()) {
          onAllow();
          return;
        }
        queueLeave({ type: 'callback', onAllow });
      },
    };
    return () => {
      leaveGuardRef.current = null;
    };
  }, [leaveGuardRef, hasUnsavedChanges, queueLeave]);

  React.useEffect(() => {
    if (!isDirty) {
      allowLeaveRef.current = false;
      return;
    }

    const originalPush = window.history.pushState.bind(window.history);
    const originalReplace = window.history.replaceState.bind(window.history);
    const here = () =>
      `${window.location.pathname}${window.location.search}${window.location.hash}`;

    if (!trapPushedRef.current) {
      trapPushedRef.current = true;
      wizardUrlRef.current = here();
      originalPush(window.history.state, '', wizardUrlRef.current);
    }

    allowLeaveRef.current = false;

    const onPopState = () => {
      if (allowLeaveRef.current || !hasUnsavedChanges()) return;
      queueLeave({ type: 'history' });
    };
    window.addEventListener('popstate', onPopState);

    const maybeBlock = (
      original: History['pushState'],
      data: unknown,
      unused: string,
      url?: string | URL | null
    ) => {
      if (allowLeaveRef.current) {
        original(data, unused, url);
        return;
      }
      const next = historyUrl(url);
      if (!next || next === here()) {
        original(data, unused, url);
        return;
      }
      const historyState = data as { usr?: unknown } | null;
      queueLeave({ type: 'href', href: next, state: historyState?.usr });
    };

    window.history.pushState = (data, unused, url) => {
      maybeBlock(originalPush, data, unused, url);
    };
    window.history.replaceState = (data, unused, url) => {
      maybeBlock(originalReplace, data, unused, url);
    };

    return () => {
      window.removeEventListener('popstate', onPopState);
      window.history.pushState = originalPush;
      window.history.replaceState = originalReplace;
    };
  }, [hasUnsavedChanges, isDirty, queueLeave]);

  // Calculate completed steps based on profile data
  const calculateCompletedSteps = (profile: StartupProfile | InvestorProfile | null): number[] => {
    const completed: number[] = [];

    if (!profile) return completed;

    // Use lastCompletedStep to determine which steps are completed
    // Steps 0 to lastCompletedStep are completed
    const lastCompletedStep =
      'lastCompletedStep' in profile ? (profile.lastCompletedStep ?? -1) : -1;

    if (lastCompletedStep >= 0) {
      for (let i = 0; i <= lastCompletedStep; i++) {
        completed.push(i);
      }
    }

    return completed;
  };

  const completedSteps = calculateCompletedSteps(currentProfile);
  const lastCompletedStep =
    currentProfile && 'lastCompletedStep' in currentProfile
      ? (currentProfile.lastCompletedStep ?? -1)
      : -1;
  const expressSignupCompleted = Boolean(
    userType === 'investor' &&
      currentProfile &&
      (currentProfile as { expressSignupCompletedAt?: unknown }).expressSignupCompletedAt
  );
  const maxReachableStep = maxReachableWizardStep(
    lastCompletedStep,
    steps.length,
    isProfileComplete(currentProfile),
    expressSignupCompleted
  );

  // Reset navigation flag when location actually changes
  // Use a small delay to ensure all effects have finished executing
  React.useEffect(() => {
    if (isNavigatingRef.current && navigatingToPathRef.current === location.pathname) {
      // console.log('✅ Navigation completed to:', location.pathname);
      // Delay reset to ensure all effects have finished
      const timeoutId = setTimeout(() => {
        isNavigatingRef.current = false;
        navigatingToPathRef.current = null;
        // Reset last navigated path after a delay to allow effects to settle
        setTimeout(() => {
          lastNavigatedPathRef.current = location.pathname;
        }, 100);
        // console.log('🔄 Navigation flag reset');
      }, 300);
      return () => clearTimeout(timeoutId);
    }
  }, [location.pathname]);

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (navigateTimeoutRef.current) {
        clearTimeout(navigateTimeoutRef.current);
      }
    };
  }, []);

  // REMOVED: State → URL effect
  // We only navigate manually from handleSave and handleStepClick
  // URL is the single source of truth, URL → State effect keeps currentStep in sync

  const handleNext = async () => {
    if (user && currentStep < steps.length - 1) {
      try {
        await updateProfile(user.id, userType, {}, currentStep + 1);
      } catch (error) {
        console.error('Error updating step:', error);
      }
    }
  };

  const goToStep = (stepIndex: number) => {
    if (userType === 'investor' && steps[stepIndex]?.id === 'matching-starts') {
      navigate('/dashboard', { replace: true });
      return;
    }

    if (embedded) {
      setCurrentStep(stepIndex);
      return;
    }

    const stepSlug = steps[stepIndex]?.id;
    if (!stepSlug) return;

    const nextPath = `/profile/${userType}/${stepSlug}`;
    isNavigatingRef.current = true;
    navigatingToPathRef.current = nextPath;
    setCurrentStep(stepIndex);
    navigate(nextPath, { replace: true, state: editMode ? { editMode: true } : undefined });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  React.useEffect(() => {
    if (embedded || loading || isInitialLoad) return;
    if (currentStep <= maxReachableStep) return;
    goToStep(maxReachableStep);
    // goToStep is recreated each render; including it would re-run this on every paint.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [embedded, loading, isInitialLoad, currentStep, maxReachableStep]);

  const handlePrevious = () => {
    if (currentStep <= 0) return;
    const prevStep = currentStep - 1;
    if (hasUnsavedChanges()) {
      queueLeave({ type: 'step', index: prevStep });
      return;
    }
    goToStep(prevStep);
  };

  const handleStepClick = (stepIndex: number) => {
    if (!user) {
      console.warn('Cannot navigate - user not available');
      return;
    }

    if (stepIndex === currentStep || stepIndex > maxReachableStep) return;

    if (hasUnsavedChanges()) {
      queueLeave({ type: 'step', index: stepIndex });
      return;
    }

    goToStep(stepIndex);
  };

  const markStepCompleted = async (stepIndex: number) => {
    // This function is now handled automatically by calculateCompletedSteps
    // based on the profile data, so we don't need to do anything here
    // console.log(`Step ${stepIndex} completed - this is now calculated automatically`);
  };

  const handleCreateProfile = async (formData: any) => {
    if (user) {
      try {
        // Create profile with initial data from first step
        await createProfile(user.id, userType, formData);
        // console.log('Profile created successfully!');
      } catch (error) {
        console.error('Error creating profile:', error);
      }
    }
  };

  const [validationPassed, setValidationPassed] = React.useState(false);
  const validationResultRef = React.useRef<boolean | null>(null);
  // Form snapshot from the step, taken before Save refreshes the account.
  const validatedFormRef = React.useRef<any>(null);
  // Resolver for the in-flight validation promise
  const pendingValidationResolveRef = React.useRef<((passed: boolean) => void) | null>(null);

  const handleValidationResult = React.useCallback((passed: boolean, latestFormData?: any) => {
    if (latestFormData && typeof latestFormData === 'object') {
      validatedFormRef.current = latestFormData;
    }
    setValidationPassed(passed);
    validationResultRef.current = passed;
    // Clear global error if validation passes
    if (passed) {
      setValidationError(null);
    }
    // Resolve the pending promise if handleSave is waiting
    if (pendingValidationResolveRef.current) {
      pendingValidationResolveRef.current(passed);
      pendingValidationResolveRef.current = null;
    }
  }, []);

  // Memoize onCreateProfile to prevent infinite loops in child components
  const handleCreateProfileUpdate = React.useCallback(
    (formData: any) => {
      setCurrentFormData(formData);
      const serialized = stableStringify(formData);
      if (!dirtyFrozenRef.current || baselineRef.current == null) {
        baselineRef.current = serialized;
        return;
      }
      if (serialized !== baselineRef.current) {
        markDirty(true);
        return;
      }
      if (!userEditedRef.current) markDirty(false);
    },
    [markDirty]
  );

  // Clear validation error when validation passes (user fixes errors)
  React.useEffect(() => {
    if (validationPassed && validationError) {
      setValidationError(null);
    }
  }, [validationPassed, validationError]);

  // Clear validation error when user makes changes (indicating they're fixing issues)
  React.useEffect(() => {
    if (validationError && currentFormData) {
      // For Industry step, clear error when amount becomes valid (> 0)
      if (currentStep === 1) {
        setValidationError(null);
      }
      // For Company step, clear error only when all required fields including city/state/SDG are answered
      if (currentStep === 0) {
        const sdgAnswered = typeof currentFormData.sustainableDevelopmentGoal === 'boolean';
        const country = String(currentFormData.country || '');
        const subdivisionOk =
          !countryRequiresStateSubdivision(country) ||
          Boolean(String(currentFormData.state || '').trim());
        const cityOk = Boolean(String(currentFormData.city || '').trim());
        if (
          sdgAnswered &&
          currentFormData.companyName &&
          currentFormData.url &&
          currentFormData.country &&
          subdivisionOk &&
          cityOk &&
          currentFormData.slogan &&
          currentFormData.pitchSummary &&
          String(currentFormData.pitchSummary).trim().length >= 80 &&
          currentFormData.pitchDeckUrl
        ) {
          // User is making changes, clear error to give them feedback
          setValidationError(null);
        }
      }
      // Founders step: clear once every founder has name, title, country, and ownership.
      if (userType === 'startup' && steps[currentStep]?.id === 'founders-team') {
        const founders = Array.isArray(currentFormData.foundersData)
          ? currentFormData.foundersData
          : [];
        const teamMembers = Array.isArray(currentFormData.teamMembersData)
          ? currentFormData.teamMembersData
          : [];
        const teamCountRaw = String(currentFormData.teamMembersCount || '0');
        const teamRequired = teamCountRaw === '+3' ? 4 : Math.max(0, Number(teamCountRaw) || 0);
        const foundersComplete =
          Boolean(currentFormData.companyFounders) &&
          founders.length > 0 &&
          founders.every(
            (founder: {
              name?: string;
              jobTitle?: string;
              country?: string;
              ownership?: string;
            }) => {
              const nameOk = typeof founder?.name === 'string' && founder.name.trim() !== '';
              const titleOk =
                typeof founder?.jobTitle === 'string' && founder.jobTitle.trim() !== '';
              const countryOk =
                typeof founder?.country === 'string' && founder.country.trim() !== '';
              const ownership =
                typeof founder?.ownership === 'string' ? founder.ownership.trim() : '';
              const ownershipOk = /^\d{1,3}$/.test(ownership) && Number(ownership) <= 100;
              return nameOk && titleOk && countryOk && ownershipOk;
            }
          );
        const teamComplete =
          teamRequired === 0 ||
          (teamMembers.length >= teamRequired &&
            teamMembers
              .slice(0, teamRequired)
              .every(
                (member: { name?: string; jobTitle?: string }) =>
                  typeof member?.name === 'string' &&
                  member.name.trim() !== '' &&
                  typeof member?.jobTitle === 'string' &&
                  member.jobTitle.trim() !== ''
              ));
        if (foundersComplete && teamComplete) {
          setValidationError(null);
        }
      }
      // Ownership step: clear once capitalization + impressive factor are filled.
      if (userType === 'startup' && steps[currentStep]?.id === 'ownership-traction') {
        const impressiveFactor = String(currentFormData.impressiveFactor || '').trim();
        const capitalization = String(currentFormData.capitalization || '').trim();
        if (capitalization && impressiveFactor.length >= IMPRESSIVE_FACTOR_MIN_CHARS) {
          setValidationError(null);
        }
      }
    }
  }, [currentFormData, validationError, currentStep, steps, userType]);

  // Mark the last step (matching-starts) as completed when user reaches it
  // Use ref to prevent multiple calls
  const hasMarkedLastStepRef = React.useRef(false);
  React.useEffect(() => {
    const lastStepIndex = steps.length - 1;

    // Reset ref if we're not on the last step anymore
    if (currentStep !== lastStepIndex) {
      hasMarkedLastStepRef.current = false;
      return;
    }

    // Only mark if all conditions are met and we haven't already marked it
    if (
      currentStep === lastStepIndex &&
      user &&
      currentProfile &&
      !hasMarkedLastStepRef.current &&
      (currentProfile.lastCompletedStep ?? -1) < lastStepIndex
    ) {
      hasMarkedLastStepRef.current = true;
      // Mark the last step as completed by explicitly updating lastCompletedStep
      updateProfile(user.id, userType, { lastCompletedStep: lastStepIndex }, lastStepIndex).catch(
        error => {
          console.error('Error marking last step as completed:', error);
          hasMarkedLastStepRef.current = false; // Reset on error so we can retry
        }
      );
    }
  }, [currentStep, user, currentProfile, steps.length, updateProfile, userType]);

  const handleSave = async () => {
    if (isSaving) return;

    // First check if form data exists (validation happens in each step)
    if (!currentFormData) {
      return;
    }

    if (!user) {
      return;
    }

    // Reset validation state
    setValidationPassed(false);
    validationResultRef.current = null;
    validatedFormRef.current = null;
    setIsSaving(true);

    // Create a Promise that resolves when the child step calls onValidationResult
    // console.log('🔍 Triggering validation...');
    const validationResult = await new Promise<boolean>(resolve => {
      pendingValidationResolveRef.current = resolve;
      setShouldValidate(true);
    });

    // console.log('✅ Validation result - isValid:', validationResult);

    // Reset validation trigger
    setShouldValidate(false);

    const formToSave = validatedFormRef.current || currentFormData;
    const isStartupCompanyStep = userType === 'startup' && steps[currentStep]?.id === 'company';
    const sdgAnswered = typeof formToSave.sustainableDevelopmentGoal === 'boolean';

    // If validation didn't pass, show error and stop
    if (!validationResult || (isStartupCompanyStep && !sdgAnswered)) {
      setValidationError('Complete the highlighted fields to continue');
      setIsSaving(false);
      return;
    }

    setValidationError(null);
    allowLeaveRef.current = true;

    // If validation passed, save the profile
    try {
      // console.log('About to save profile. currentProfile exists:', !!currentProfile);

      // Embedded mode (profile / Settings edit): save advances to the next
      // section. Last real section (before Matching Starts) exits via onSave.
      if (embedded) {
        const nextStep = currentStep + 1;
        const advanceToNext =
          currentStep < steps.length - 1 && steps[nextStep]?.id !== 'matching-starts';

        if (!currentProfile) {
          await createProfile(user.id, userType, formToSave);
        } else {
          await updateProfile(
            user.id,
            userType,
            formToSave,
            advanceToNext ? nextStep : currentStep
          );
        }

        if (advanceToNext) {
          markDirty(false);
          dirtyFrozenRef.current = false;
          baselineRef.current = stableStringify(formToSave);
          userEditedRef.current = false;
          isDirtyRef.current = false;
          setCurrentFormData(null);
          goToStep(nextStep);
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }

        onSave?.({
          ...(currentProfile ?? {}),
          ...formToSave,
        } as StartupProfile);

        return;
      }

      // editMode (deep-linked step edit): save and return to the profile page.
      if (editMode && currentProfile) {
        await updateProfile(user.id, userType, formToSave, currentStep);
        if (userType === 'startup') {
          navigate('/profile/startup/view', { replace: true });
        } else {
          navigate('/dashboard', {
            replace: true,
            state: { toastMessage: 'You’ve successfully updated your profile' },
          });
        }
        return;
      }

      if (!currentProfile) {
        // Create new profile
        if (currentStep < steps.length - 1) {
          const nextStep = currentStep + 1;
          const nextStepSlug = steps[nextStep].id;
          const nextPath = `/profile/${userType}/${nextStepSlug}`;

          if (userType === 'investor' && nextStep === steps.length - 1) {
            await createProfile(user.id, userType, formToSave);
            await updateProfile(
              user.id,
              userType,
              { lastCompletedStep: nextStep, profileCompleted: true },
              nextStep
            );
            navigate('/dashboard', {
              replace: true,
              state: { toastMessage: 'You’ve successfully updated your profile' },
            });
            return;
          }

          if (userType === 'startup' && nextStep === steps.length - 1) {
            await createProfile(user.id, userType, formToSave);
            await updateProfile(
              user.id,
              userType,
              { lastCompletedStep: nextStep, profileCompleted: true },
              nextStep
            );
            // First completion → profile view with the “how investors see you” popup.
            navigate('/profile/startup/view', {
              replace: true,
              state: { showProfileReadyPopup: true },
            });
            return;
          }

          isNavigatingRef.current = true;
          navigatingToPathRef.current = nextPath;

          await createProfile(user.id, userType, formToSave);
          await updateProfile(user.id, userType, {}, nextStep);

          lastNavigatedPathRef.current = nextPath;
          navigate(nextPath, { replace: true, state: editMode ? { editMode: true } : undefined });
          window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
          await createProfile(user.id, userType, formToSave);
          if (userType === 'investor') {
            navigate('/dashboard', {
              replace: true,
              state: { toastMessage: 'You’ve successfully updated your profile' },
            });
            return;
          }
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      } else {
        // Update existing profile
        if (currentStep < steps.length - 1) {
          const nextStep = currentStep + 1;
          const nextStepSlug = steps[nextStep].id;
          const nextPath = `/profile/${userType}/${nextStepSlug}`;

          if (userType === 'investor' && nextStep === steps.length - 1) {
            await updateProfile(
              user.id,
              userType,
              { ...formToSave, lastCompletedStep: nextStep, profileCompleted: true },
              nextStep
            );
            navigate('/dashboard', {
              replace: true,
              state: { toastMessage: 'You’ve successfully updated your profile' },
            });
            return;
          }

          if (userType === 'startup' && nextStep === steps.length - 1) {
            const firstCompletion = !isProfileComplete(currentProfile);
            await updateProfile(
              user.id,
              userType,
              { ...formToSave, lastCompletedStep: nextStep, profileCompleted: true },
              nextStep
            );
            // First completion → profile view + investor-preview popup. Later finishes just land on profile.
            navigate('/profile/startup/view', {
              replace: true,
              state: firstCompletion ? { showProfileReadyPopup: true } : undefined,
            });
            return;
          }

          isNavigatingRef.current = true;
          navigatingToPathRef.current = nextPath;

          await updateProfile(user.id, userType, formToSave, nextStep);

          lastNavigatedPathRef.current = nextPath;
          navigate(nextPath, { replace: true, state: editMode ? { editMode: true } : undefined });
          window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
          await updateProfile(user.id, userType, formToSave, currentStep);
          if (userType === 'investor') {
            navigate('/dashboard', {
              replace: true,
              state: { toastMessage: 'You’ve successfully updated your profile' },
            });
            return;
          }
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }
      // console.log('Profile saved successfully!');
    } catch (error) {
      allowLeaveRef.current = false;
      console.error('Error saving profile:', error);
      const message = error instanceof Error ? error.message : 'Could not save. Please try again.';
      setValidationError(message);
    } finally {
      setIsSaving(false);
    }
  };

  const discardAndLeave = () => {
    if (isSaving) return;
    const leave = pendingLeaveRef.current;
    leaveRequestRef.current += 1;
    pendingLeaveRef.current = null;
    pendingValidationResolveRef.current = null;
    validatedFormRef.current = null;
    setLeaveDialogOpen(false);
    setValidationError(null);
    setShouldValidate(false);
    userEditedRef.current = false;
    isDirtyRef.current = false;
    dirtyFrozenRef.current = false;
    baselineRef.current = null;
    setIsDirty(false);
    setCurrentFormData(null);
    if (!leave) return;
    completeLeave(leave);
  };

  const dismissLeaveDialog = () => {
    const pending = pendingLeaveRef.current;
    leaveRequestRef.current += 1;
    pendingLeaveRef.current = null;
    setLeaveDialogOpen(false);
    if (pending?.type === 'history') {
      allowLeaveRef.current = true;
      const url =
        wizardUrlRef.current ||
        `${window.location.pathname}${window.location.search}${window.location.hash}`;
      window.history.pushState(window.history.state, '', url);
      allowLeaveRef.current = false;
      trapPushedRef.current = true;
    }
  };

  const completeLeave = (leave: PendingLeave) => {
    allowLeaveRef.current = true;
    if (leave.type === 'step') {
      goToStep(leave.index);
      return;
    }
    if (leave.type === 'history') {
      userEditedRef.current = false;
      isDirtyRef.current = false;
      trapPushedRef.current = false;
      window.history.back();
      return;
    }
    if (leave.type === 'callback') {
      userEditedRef.current = false;
      isDirtyRef.current = false;
      setIsDirty(false);
      leave.onAllow();
      return;
    }
    navigate(leave.href, leave.state !== undefined ? { state: leave.state } : undefined);
  };

  const saveAndLeave = async () => {
    if (isSaving) return;
    const leave = pendingLeaveRef.current;
    if (!leave || !currentFormData || !user) {
      dismissLeaveDialog();
      return;
    }

    const requestId = leaveRequestRef.current;
    setIsSaving(true);
    setValidationPassed(false);
    validationResultRef.current = null;
    validatedFormRef.current = null;
    const validationResult = await new Promise<boolean>(resolve => {
      pendingValidationResolveRef.current = resolve;
      setShouldValidate(true);
    });
    setShouldValidate(false);
    if (requestId !== leaveRequestRef.current) {
      setIsSaving(false);
      return;
    }

    const formToSave = validatedFormRef.current || currentFormData;
    const isStartupCompanyStep = userType === 'startup' && steps[currentStep]?.id === 'company';
    const sdgAnswered = typeof formToSave.sustainableDevelopmentGoal === 'boolean';
    if (!validationResult || (isStartupCompanyStep && !sdgAnswered)) {
      setValidationError('Complete the highlighted fields to continue');
      setIsSaving(false);
      dismissLeaveDialog();
      return;
    }

    setValidationError(null);
    try {
      if (!currentProfile) {
        await createProfile(user.id, userType, formToSave);
      } else {
        await updateProfile(user.id, userType, formToSave);
      }
      if (requestId !== leaveRequestRef.current) return;
      pendingLeaveRef.current = null;
      setLeaveDialogOpen(false);
      markDirty(false);
      dirtyFrozenRef.current = false;
      baselineRef.current = stableStringify(formToSave);
      completeLeave(leave);
    } catch (error) {
      console.error('Error saving profile:', error);
      const message = error instanceof Error ? error.message : 'Could not save. Please try again.';
      setValidationError(message);
      dismissLeaveDialog();
    } finally {
      setIsSaving(false);
    }
  };

  // Show loading state while fetching profile data on initial load
  if (loading || isInitialLoad) {
    return (
      <div
        className={`${embedded ? '' : 'min-h-screen'} bg-dark-950 flex items-center justify-center`}
      >
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500 mb-4"></div>
          <p className="text-white text-lg">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`${embedded ? '' : 'min-h-screen'} bg-dark-950`}>
      <div
        className={`w-[90%] sm:container sm:mx-auto sm:px-4 ${steps[currentStep].id === 'matching-starts' ? 'pt-1 sm:pt-2' : 'pt-4 sm:pt-6 md:pt-8'} ${embedded ? 'pb-6' : 'pb-16 sm:pb-20'} mx-auto`}
      >
        {/* Breadcrumbs */}
        <ProfileBreadcrumbs
          steps={steps}
          currentStep={currentStep}
          completedSteps={completedSteps}
          onStepClick={handleStepClick}
          userType={userType}
          embedded={embedded}
          maxReachableStep={maxReachableStep}
          matchingLive={Boolean(
            ((currentProfile as { approved?: boolean; profileCompleted?: boolean } | null)
              ?.approved === true ||
              (currentProfile as { profileCompleted?: boolean } | null)?.profileCompleted ===
                true) &&
              !(currentProfile as { paused?: boolean } | null)?.paused
          )}
        />

        {/* Step Content */}
        <div onInput={noteUserEdit} onChange={noteUserEdit} onClick={noteUserEdit}>
          <ProfileStep
            step={steps[currentStep]}
            stepIndex={currentStep}
            userType={userType}
            onComplete={() => markStepCompleted(currentStep)}
            onCreateProfile={handleCreateProfileUpdate}
            hasProfile={!!currentProfile}
            shouldValidate={shouldValidate}
            onValidationHandled={() => setShouldValidate(false)}
            onValidationResult={handleValidationResult}
          />
        </div>

        {/* Navigation - Hide on last step (matching-starts) */}
        {steps[currentStep].id !== 'matching-starts' && (
          <div>
            <ProfileNavigation
              currentStep={currentStep}
              totalSteps={steps.length}
              onNext={handleSave}
              onPrevious={handlePrevious}
              canProceed={true}
              isSaving={isSaving}
              buttonText={
                steps[currentStep].id === 'external-support' ? 'SAVE AND FINISH' : undefined
              }
            />
            {validationError && (
              <div className="flex justify-center mt-4">
                <p className="text-red-500 text-sm">{validationError}</p>
              </div>
            )}
          </div>
        )}
      </div>
      <UnsavedProfileChangesDialog
        isOpen={leaveDialogOpen}
        isSaving={isSaving}
        onCancel={dismissLeaveDialog}
        onDiscard={discardAndLeave}
        onSave={saveAndLeave}
      />
    </div>
  );
};

export default ProfileWizard;
